export type ImageResource = {
  url: string;
  isComplete: boolean;
  element: HTMLImageElement;
  error: Event | string;
} & {readonly brand: unique symbol};

// eslint-disable-next-line no-redeclare
export const ImageResource = {
  create: ({
    url,
    isComplete,
    element,
    error,
  }: {
    url: string;
    isComplete?: boolean;
    element?: HTMLImageElement;
    error?: Event | string;
  }): ImageResource => {
    return {
      element,
      error,
      isComplete,
      url,
    } as ImageResource;
  },
};

type ResourceMap = Map<string, ImageResource>;

export class ImageLoader {
  static singleton: ImageLoader;
  resources: Map<string, ImageResource> = new Map();

  static create() {
    if (ImageLoader.singleton) {
      return ImageLoader.singleton;
    }
    return (ImageLoader.singleton = new ImageLoader());
  }

  public add(url: string): ImageLoader {
    const resource = this.find(url);
    if (!resource) {
      this.resources.set(url, ImageResource.create({url}));
    }
    return this;
  }

  public get loaded() {
    const ret = Array.from(this.resources.values()).some((resource) => {
      return !resource.isComplete;
    });
    return !ret;
  }

  public async load(
    onLoaded?: (resources: ResourceMap) => void,
  ): Promise<ResourceMap> {
    const imagesPromise = Array.from(this.resources.values()).map(
      (resource) => {
        return !resource.isComplete && this.imageLoad(resource);
      },
    );
    await Promise.all<any>(imagesPromise);
    onLoaded?.(this.resources);
    return this.resources;
  }

  public async get(url: string): Promise<ImageResource | undefined> {
    const resource = this.findComplete(url);
    if (!resource) {
      return new Promise((resolve) => {
        this.add(url).load((resources) => {
          const found = Array.from(resources.values()).find(
            (resource) => this.getUrl(resource?.url) === this.getUrl(url),
          );
          resolve(found);
        });
      });
    } else {
      return resource;
    }
  }

  private getUrl(url: string): string {
    return url.replace('http:', '').replace(/(localhost):[0-9]{4}/, '$1');
  }

  private find(url: string): ImageResource | undefined {
    return Array.from(this.resources.values()).find((resource) => {
      // FIXME: プロトコルとポートをurlから削除して比較
      // 開発時にbrowserSync+Pjax時にURLが変わってしまう
      const itemSrc = this.getUrl(resource.url);
      const elementSrc = this.getUrl(url);
      return itemSrc === elementSrc;
    });
  }

  private findComplete(url: string): ImageResource | undefined {
    const ret = this.find(url);
    return ret?.isComplete ? ret : undefined;
  }

  private imageLoad(resource: ImageResource) {
    return new Promise((resolve) => {
      const img = new Image();
      // img.crossOrigin = 'anonymous';
      img.onload = () => {
        const ret = ImageResource.create({
          ...resource,
          element: img,
          isComplete: true,
        });
        this.resources.set(ret.url, ret);
        resolve(ret);
      };
      img.onerror = (error) => {
        const ret = ImageResource.create({
          ...resource,
          element: img,
          error,
        });
        this.resources.set(ret.url, ret);
        resolve(ret);
      };
      img.src = resource.url;
    });
  }

}
